import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

// Perf : l'ajout rapide ne télécharge plus la fiche produit entière (≈900 Ko de HTML).
// On retient, par modèle de fiche produit, l'identifiant de sa section principale, puis on
// demande uniquement cette section à Shopify (Section Rendering API, ≈130 Ko).
const SECTION_IDS_STORAGE_KEY = 'quick-add-main-section-ids';

/** @returns {Record<string, string>} */
function readMainSectionIds() {
  try {
    return JSON.parse(sessionStorage.getItem(SECTION_IDS_STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/**
 * @param {string} templateKey
 * @param {string} sectionId
 */
function rememberMainSectionId(templateKey, sectionId) {
  try {
    const ids = readMainSectionIds();
    ids[templateKey] = sectionId;
    sessionStorage.setItem(SECTION_IDS_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Stockage indisponible (navigation privée…) : on retombe sur la page complète
  }
}

/**
 * Extrait la section principale (…__main) d'une page produit complète et ne parse que ce fragment.
 * @param {string} pageHtml
 * @returns {{ sectionId: string | null, doc: Document }}
 */
function parseMainSection(pageHtml) {
  const match = pageHtml.match(/<[a-z]+[^>]*\bid="shopify-section-(template--[^"]+__main)"/);
  if (!match || match.index === undefined) {
    return { sectionId: null, doc: new DOMParser().parseFromString(pageHtml, 'text/html') };
  }
  const end = pageHtml.indexOf('id="shopify-section-', match.index + match[0].length);
  const fragment = pageHtml.slice(match.index, end === -1 ? undefined : pageHtml.lastIndexOf('<', end));
  return { sectionId: match[1] ?? null, doc: new DOMParser().parseFromString(fragment, 'text/html') };
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();

  get templateKey() {
    return this.dataset.templateSuffix || 'default';
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    // Perf : on commence à charger dès que le doigt / la souris appuie, avant la fin du clic
    this.addEventListener('pointerdown', this.#prefetch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerdown', this.#prefetch);
    this.#abortController?.abort();
  }

  /** @param {PointerEvent} event */
  #prefetch = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.quick-add__button--choose')) return;
    const url = this.productPageUrl;
    if (url) this.fetchProductPage(url);
  };

  /**
   * Préchargement appelé par product-card (survol sur ordinateur, changement de variante) :
   * remplit le cache de l'ajout rapide au lieu de télécharger la page pour rien.
   * @param {string} productPageUrl
   */
  fetchProductPage(productPageUrl) {
    if (!productPageUrl || this.#cachedContent.has(productPageUrl)) return Promise.resolve(null);
    return this.#loadProductGrid(productPageUrl).catch(() => null);
  }

  /**
   * Charge (une seule fois par URL) la grille produit à afficher dans la fenêtre d'ajout rapide.
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(url) {
    const pending = this.#pendingContent.get(url);
    if (pending) return pending;

    const promise = this.fetchProductGrid(url)
      .then((gridElement) => {
        // Cache the cloned element to avoid modifying the original
        const productGrid = gridElement ? /** @type {Element} */ (gridElement.cloneNode(true)) : null;
        if (productGrid) this.#cachedContent.set(url, productGrid);
        return productGrid;
      })
      .finally(() => this.#pendingContent.delete(url));

    this.#pendingContent.set(url, promise);
    return promise;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content (réutilise le chargement lancé au pointerdown)
      productGrid = (await this.#loadProductGrid(currentUrl)) ?? undefined;
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Récupère la grille produit : d'abord la section seule si son identifiant est connu,
   * sinon la page complète (dont on ne parse que la section principale).
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Element | null>}
   */
  async fetchProductGrid(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    const { signal } = abortController;

    try {
      const knownSectionId = readMainSectionIds()[this.templateKey];
      if (knownSectionId) {
        const sectionUrl = new URL(productPageUrl);
        sectionUrl.searchParams.set('section_id', knownSectionId);
        const response = await fetch(sectionUrl, { signal });
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = doc.querySelector('[data-product-grid-content]');
          if (grid) return grid;
        }
        // Identifiant périmé (thème republié…) : on repasse par la page complète
      }

      const response = await fetch(productPageUrl, { signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const { sectionId, doc } = parseMainSection(await response.text());
      const grid = doc.querySelector('[data-product-grid-content]');
      if (grid && sectionId) rememberMainSectionId(this.templateKey, sectionId);

      return grid;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === abortController) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
